import { getAdminUserQrNotesQuery } from '@/api/qr-codes/qrCodeQueries';
import type {
  UserQrNote,
  UserQrNoteAssigned,
} from '@/api/qr-codes/qrCodeTypes';
import { DialogWrapper } from '@/components/common/DialogWrapper';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/common/Table';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { DashboardTableAccordion } from '../DashboardTableAccordion';
import { ModifyUserCart } from '../users/show-patient/ModifyUserCart';

export default function ScannedUsersTable() {
  const [page, setPage] = useState(1);

  const { data: response, isLoading } = useQuery(
    getAdminUserQrNotesQuery(page),
  );

  const users = response?.data ?? [];
  const meta = response?.meta;

  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedNotes, setSelectedNotes] = useState<Array<UserQrNoteAssigned>>(
    [],
  );

  const [isCreateOrderOpen, setIsCreateOrderOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{
    id: number;
    name: string;
    surname: string;
  } | null>(null);

  const handleExpandNotes = (user: UserQrNote) => {
    setSelectedNotes(user.assigned_notes);
    setNotesDialogOpen(true);
  };

  const handleCreateCart = (user: UserQrNote) => {
    setSelectedUser({
      id: user.id,
      name: user.name,
      surname: user.surname,
    });
    setIsCreateOrderOpen(true);
  };

  return (
    <>
      <DashboardTableAccordion
        value="scanned-users"
        title="Manage users who scanned Qr codes"
        data={users}
        isLoading={isLoading}
        emptyMessage="No scanned users found."
        loadingMessage="Loading scanned users..."
        paginationMeta={meta}
        onPageChange={setPage}
        renderHeader={() => (
          <>
            <TableHead>User</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Phone number</TableHead>
            <TableHead>Notes</TableHead>
            <TableHead className="text-right"></TableHead>
          </>
        )}
        renderRow={(user) => (
          <>
            <TableCell>
              {user.name} {user.surname}
            </TableCell>
            <TableCell>{user.email}</TableCell>
            <TableCell>{user.phone}</TableCell>
            <TableCell>
              {user.assigned_notes.length > 0 ? (
                <RelationActionButton onClick={() => handleExpandNotes(user)}>
                  Expand notes
                </RelationActionButton>
              ) : null}
            </TableCell>
            <TableCell className="text-right">
              <RelationActionButton onClick={() => handleCreateCart(user)}>
                Create cart
              </RelationActionButton>
            </TableCell>
          </>
        )}
      />

      <DialogWrapper
        open={notesDialogOpen}
        onOpenChange={setNotesDialogOpen}
        title="User's notes"
        maxWidth="lg"
      >
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Date created</TableHead>
              <TableHead>Note</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {selectedNotes.map((note) => (
              <TableRow key={note.note_id}>
                <TableCell>{note.associated_at}</TableCell>
                <TableCell>{note.notes}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogWrapper>

      <ModifyUserCart
        key={selectedUser?.id ?? 'empty'}
        open={isCreateOrderOpen}
        onOpenChange={setIsCreateOrderOpen}
        selectedUser={selectedUser}
        initialAdding={true}
      />
    </>
  );
}
